import React from 'react'
import { alpha, Box, Chip, Typography, Tooltip, Button, useTheme, Grid, CircularProgress, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import MassUpdateTypeSelect from './MassUpdateTypeSelect'
import { taskMasterStatusOptions, taskTypeList } from '../DataGrid/filterArrays'
import MassUpdateUserSelect from '../TeamTasks/MassUpdateUserSelect'
import TasksPopover from '../DataGrid/TasksPopover'
import MassSingleUserSelect from '../Forms/MassSingleUserSelect'
import MassDeleteDialog from '../DataGrid/MassDeleteDialog'
import { DynamicMassUpdateData, Task, User } from '../../types'

export interface MassUpdateTeamTasksProps {
  users: User[]
  selectedData: Task | null
  selectedMassData: Task[]
  setSelectedMassData: React.Dispatch<React.SetStateAction<Task[]>>
  isLoading: boolean
  massUpdateData: any
  rowSelectionModel: GridRowSelectionModel
  rowCount: number
  onChange: (e: any) => void
  onUpdate: (massUpdateData: DynamicMassUpdateData, rowSelectionModel: GridRowSelectionModel) => void
  onClear: () => void
  onClickDelete: (rowSelectionModel: GridRowSelectionModel) => void
  onClickMassDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setSelectedMassData: React.Dispatch<React.SetStateAction<Task[]>>
  ) => void
  onDelete: (setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>) => void
  onMassDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
  ) => void
  isDeleting: boolean
  onClosePopover: () => void
  tasks: Task[] | []
  anchorEl: HTMLElement | null
  context: string
  customPosition?: { top: number; left: number } | null
  hasSubtaskSelected: boolean
  onCloseMassDeletePopover: () => void
  isMassDeleteOpen: boolean
  // ... any additional props you need
}

const MassUpdateTeamTasksComponents: React.FC<MassUpdateTeamTasksProps> = ({
  users,
  selectedData,
  selectedMassData,
  setSelectedMassData,
  isLoading,
  massUpdateData,
  rowSelectionModel,
  rowCount,
  onChange,
  onUpdate,
  onClear,
  onClickDelete,
  onClickMassDelete,
  onDelete,
  onMassDelete,
  isDeleting,
  onClosePopover,
  tasks = [],
  anchorEl,
  context,
  customPosition,
  hasSubtaskSelected,
  onCloseMassDeletePopover,
  isMassDeleteOpen,
}) => {
  const theme = useTheme()
  const taskGridStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    pb: 0.5,
    pl: 1.5,
    pr: 1.5,
    pt: 1,
    mt: 0.5,
  }
  const gridDynamicSizes = {
    xs: hasSubtaskSelected ? 2.0 : 1.7,
    sm: hasSubtaskSelected ? 2.0 : 1.7,
    md: hasSubtaskSelected ? 2.0 : 1.7,
    lg: hasSubtaskSelected ? 2.0 : 1.7,
    xl: hasSubtaskSelected ? 2.0 : 1.7,
  }
  const gridButtonSizes = {
    xs: hasSubtaskSelected ? 2.9 : 2.0,
    sm: hasSubtaskSelected ? 2.9 : 2.0,
    md: hasSubtaskSelected ? 2.9 : 2.0,
    lg: hasSubtaskSelected ? 2.9 : 2.0,
    xl: hasSubtaskSelected ? 2.9 : 2.0,
  }
  const activeFields = Object.entries(massUpdateData || {}).filter(
    ([_, val]) =>
      val !== undefined &&
      val !== null &&
      (typeof val === 'string'
        ? val.trim().length > 0
        : Array.isArray(val)
        ? val.length > 0
        : typeof val === 'object' && Object.keys(val).length > 0)
  )

  const isFieldLocked = (fieldName: string) => {
    return activeFields.length > 0 && !activeFields.some(([key]) => key === fieldName)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'tasks-grid-footer-popover' : undefined
  const assigneeSource = selectedData?.assignees ?? []
  const currentAssigneeIds = Array.isArray(assigneeSource)
    ? assigneeSource.map(user => (user?.user_uuid ? user?.user_uuid : user ?? []))
    : []
  const queueUserSelections =
    Array.isArray(assigneeSource) && assigneeSource?.length > 0
      ? users?.filter(user => currentAssigneeIds.includes(user?.user_uuid)) ?? []
      : users ?? []
  const singleTaskDesignation = hasSubtaskSelected ? 'Subtask Selected' : 'Task Selected'
  const multiTaskDesignation = hasSubtaskSelected ? 'Subtasks Selected' : 'Tasks Selected'
  const taskDesignation =
    Array.from(rowSelectionModel?.ids)?.length === 1 ? singleTaskDesignation : multiTaskDesignation

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'space-between',
        bgcolor: theme.palette.background.section,
      }}
    >
      {Array.from(rowSelectionModel?.ids).length > 0 ? (
        <Grid
          container
          sx={{
            maxHeight: 'auto',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            alignItems: 'center',
            zIndex: 1,
            flexWrap: 'wrap',
            overflow: 'hidden',
            p: 1,
          }}
        >
          <Grid container>
            <Grid item xs={2} sm={2} md={2} lg={2} xl={2} sx={taskGridStyles}>
              {/* <Typography variant='h8'>{'Selected'}</Typography> */}
              <Chip
                label={Array.from(rowSelectionModel?.ids).length}
                sx={{ color: 'text.link', fontSize: 18 }}
              />
              <Typography>{taskDesignation}</Typography>
            </Grid>

            <Grid item xs={1.4} sm={1.4} md={1.4} lg={1.4} xl={1.4} sx={taskGridStyles} justifyContent="flex-end">
              <MassUpdateTypeSelect
                name="type"
                value={massUpdateData.type || ''}
                options={taskTypeList}
                displayLabel="Type"
                onChange={onChange}
                disabled={isFieldLocked('type')}
                sx={{ height: 40, minWidth: 150 }}
              />
            </Grid>
            <Grid item xs={1.4} sm={1.4} md={1.4} lg={1.4} xl={1.4} sx={taskGridStyles} justifyContent="flex-end">
              <MassUpdateTypeSelect
                name="masterStatus"
                value={massUpdateData.masterStatus || ''}
                options={taskMasterStatusOptions}
                displayLabel="Master Status"
                onChange={onChange}
                disabled={isFieldLocked('masterStatus')}
                sx={{ height: 40, minWidth: 150 }}
              />
            </Grid>
            <Grid item {...gridDynamicSizes} sx={taskGridStyles} justifyContent="flex-end">
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <MassUpdateUserSelect
                    name="assigner"
                    value={massUpdateData.assigner || []}
                    options={users} // Or pass your list of users for SEO
                    displayLabel="Assigner"
                    onChange={onChange}
                    disabled={isFieldLocked('assigner')}
                  />
                </>
              )}
            </Grid>
            {/* <Grid item {...gridDynamicSizes} sx={taskGridStyles} justifyContent='flex-end'>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  {hasSubtaskSelected ? (
                    <MultiUserSelect
                      name='assignees'
                      value={massUpdateData.assignees || []}
                      options={queueUserSelections || []}
                      displayLabel='Subtask Assignee'
                      singleSelect // ✅ restrict to 1 user
                      onChange={onChange}
                      disabled={isFieldLocked('assignees')}
                      isGridUpdate={true}
                    />
                  ) : (
                    <MassUpdateUserSelect
                      name='assignees'
                      value={massUpdateData.assignees || []}
                      options={users} // Or pass your list of users for SEO
                      displayLabel='Assignee(s)'
                      onChange={onChange}
                      disabled={isFieldLocked('assignees')}
                    />
                  )}
                </>
              )}
            </Grid> */}
            {!hasSubtaskSelected && (
              <Grid item {...gridDynamicSizes} sx={taskGridStyles} justifyContent="flex-end">
                <MassUpdateUserSelect
                  name="assignees"
                  value={massUpdateData.assignees || []}
                  options={users} // Or pass your list of users for SEO
                  displayLabel="Assignee(s)"
                  onChange={onChange}
                  disabled={isFieldLocked('assignees')}
                />
              </Grid>
            )}

            <Grid item {...gridDynamicSizes} sx={taskGridStyles} justifyContent="flex-end">
              {isLoading ? (
                <CircularProgress />
              ) : (
                <MassSingleUserSelect
                  name="queue"
                  value={massUpdateData.queue || null}
                  options={queueUserSelections ?? []}
                  displayLabel="Active Assignee"
                  disabled={isFieldLocked('queue')}
                  onChange={onChange}
                />
              )}
            </Grid>

            <Grid item {...gridButtonSizes} sx={taskGridStyles} justifyContent="flex-end">
              <Tooltip
                title={Array.from(rowSelectionModel?.ids).length === 1 ? 'Apply Update' : 'Apply Mass Update'}
                placement="top"
              >
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    onUpdate(massUpdateData, rowSelectionModel)
                    onClear()
                  }}
                  disabled={Array.from(rowSelectionModel?.ids).length === 0}
                  sx={{ height: 40 }}
                >
                  {Array.from(rowSelectionModel?.ids).length === 1 ? 'Update' : 'Mass Update'}
                </Button>
              </Tooltip>
              <Button
                color="secondary"
                variant="contained"
                onClick={onClear}
                disabled={
                  !massUpdateData?.type?.length &&
                  !massUpdateData?.masterStatus?.length &&
                  !massUpdateData?.queue &&
                  !massUpdateData?.assignees?.length
                }
                sx={{ height: 40 }}
              >
                Clear
              </Button>
              {Array.from(rowSelectionModel?.ids)?.length === 1 && (
                <Tooltip title={'Delete Task'} placement="top">
                  <IconButton color="inherit" onClick={() => onClickDelete(rowSelectionModel)}>
                    <DeleteIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              )}
              {Array.from(rowSelectionModel?.ids)?.length > 1 && (
                <Tooltip title={'Mass Delete'} placement="top">
                  <IconButton
                    color="inherit"
                    // onClick={onMassDelete}
                    onClick={() => onClickMassDelete(rowSelectionModel, setSelectedMassData)}
                  >
                    <DeleteIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            padding: 1,
            alignItems: 'center',
            minHeight: 50,
            gap: 1,
            bgcolor: theme.palette.background.section,
          }}
          justifyContent="flex-start"
        >
          <Grid item>
            <Typography variant="body2" mr={2}>
              Total Rows:
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: theme.palette.text.complete }}>
              {rowCount}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: alpha(theme.palette.text.primary, 0.6) }}>
              /
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: alpha(theme.palette.text.primary, 0.6) }}>
              {tasks.length}
            </Typography>
          </Grid>
        </Grid>
      )}
      {hasSubtaskSelected && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 85, // match the footer bar height + margin
            right: 40,
            zIndex: 1300,
            backgroundColor: theme.palette.background.section,
            border: `1px solid ${theme.palette.text.warning}`,
            borderRadius: 1,
            px: 2,
            py: 1,
            boxShadow: `0px 4px 10px ${alpha(theme.palette.text.primary, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="text.primary">
            Subtask(s) Selected
          </Typography>
        </Box>
      )}

      <TasksPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        handleConfirmDelete={onDelete}
        selectedData={selectedData}
        context={context}
        customPosition={customPosition}
      />
      <MassDeleteDialog
        id={'mass-delete-popover'}
        open={isMassDeleteOpen}
        onClose={onCloseMassDeletePopover}
        handleMassDelete={onMassDelete}
        selectedMassData={selectedMassData}
        context={context}
        customPosition={customPosition}
      />
    </Box>
  )
}

export default MassUpdateTeamTasksComponents
